import { RefObject, useEffect, useRef, useState } from 'react';

import { IN_VIEW_DEFAULT } from './constants';

export type UseInViewOnceOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  /** When false, observation is skipped (useful while data is loading). */
  enabled?: boolean;
};

/**
 * Observes an element and resolves once when it enters the viewport.
 * Disconnects immediately after the first confirmed intersection.
 */
export const useInViewOnce = <T extends HTMLElement = HTMLElement>(
  options: UseInViewOnceOptions = {}
): [RefObject<T>, boolean] => {
  const {
    root = null,
    rootMargin = IN_VIEW_DEFAULT.rootMargin,
    threshold = IN_VIEW_DEFAULT.threshold,
    enabled = true,
  } = options;

  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enabled || inView) return;

    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        // Confirm on the next frame so late layout (content injected above) can settle.
        requestAnimationFrame(() => {
          if (!ref.current) return;

          const rect = ref.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight || 0;
          const stillInView =
            rect.top < viewportHeight &&
            rect.bottom > 0 &&
            rect.top < viewportHeight * 0.92;

          if (stillInView) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { root, rootMargin, threshold }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled, inView, root, rootMargin, threshold]);

  return [ref, inView];
};
